export interface Container { [K: string]: any }

export type ContainerFactory<T extends Container> = { [K in keyof T]: (container: T) => T[K] }

/** generate a container of dependencies that are lazily initialised, so that initialisation order does not need to be manually configured
 *
 * given an container of initialisers, return a container of initialised objects
 *
 * the objects are initialised on first call, and otherwise remain as factories
 */
export default function LazyDependencyContainer<T extends Container>(factories: ContainerFactory<T>): T {
    const instances: Partial<T> = {};
    const proxy = new Proxy(factories, {
        get(target, key): T[keyof T] | undefined {
            if (!instances[key as keyof T]) {
                const factory: ContainerFactory<T>[keyof T] | undefined = target[key as keyof T];
                if (!factory) return factory;
                instances[key as keyof T] = factory(proxy);
            }
            return instances[key as keyof T];
        },
    }) as T;
    // call each of the keys once to ensure all services have been started
    for (const k in factories) proxy[k];
    return proxy;
}
